import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { avgScore, distinctionOf, DISTINCTIONS } from "../mock";
import { usePlaces } from "../context/PlacesContext";
import { PlaceRow, SectionLabel } from "../components/Bits";

export default function Reviews() {
  const { places, getPlace, loading } = usePlaces();

  if (loading) {
    return <div className="pt-[140px] pb-40 text-center text-ink-soft font-serif text-2xl">Loading…</div>;
  }

  const sorted = [...places].sort((a, b) => avgScore(b.scores) - avgScore(a.scores));
  const lead = getPlace("the-hearth-inglewood") || sorted[0];
  const leadLevel = lead ? distinctionOf(lead) : 0;
  const rest = sorted.filter((p) => p.slug !== lead?.slug);

  return (
    <div className="pt-[68px]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <SectionLabel>Reviews</SectionLabel>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-semibold mt-4 display-balance max-w-3xl">
          Detailed verdicts, honestly earned.
        </h1>
        <p className="text-ink-soft mt-5 max-w-xl text-lg">
          Every review is written after an anonymous, in-person visit — on our dime, never the venue's.
        </p>

        {lead && (
          <Link to={`/place/${lead.slug}`} className="group grid lg:grid-cols-2 gap-10 mt-14 items-center">
            <div className="img-zoom relative aspect-[4/3] bg-ink/5">
              <img src={lead.image} alt={lead.name} className="w-full h-full object-cover" />
              {leadLevel > 0 && (
                <span className="absolute top-5 left-5 flex items-center gap-1.5 bg-ink/40 backdrop-blur-sm px-3 py-2">
                  {Array.from({ length: leadLevel }).map((_, i) => (
                    <img key={i} src="/placeworthy-mark.png" alt="" aria-hidden="true" className="invert w-6 h-6" />
                  ))}
                </span>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 text-ink-soft text-sm">
                <span>{lead.category}</span>
                <span className="w-1 h-1 rounded-full bg-ink-soft/50" />
                <span>{lead.neighborhood} · {lead.city}</span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold mt-3 group-hover:text-claret transition-colors">{lead.name}</h2>
              {leadLevel > 0 && (
                <div className="flex items-center gap-2.5 mt-4">
                  <span className="flex items-center gap-1">
                    {Array.from({ length: leadLevel }).map((_, i) => (
                      <img key={i} src="/placeworthy-mark.png" alt="" aria-hidden="true" className="w-7 h-7" />
                    ))}
                  </span>
                  <span className="font-serif text-2xl font-semibold text-claret">{DISTINCTIONS[leadLevel].label}</span>
                </div>
              )}
              <p className="text-ink-soft mt-5 text-lg leading-relaxed">{lead.excerpt}</p>
              <span className="inline-flex items-center gap-2 mt-6 text-sm font-semibold link-underline">
                Read the full review <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        )}

        <div className="mt-20">
          <h3 className="font-serif text-2xl font-semibold mb-2">All reviews</h3>
          <div className="border-t border-ink/10">
            {rest.map((p) => <PlaceRow key={p.id} place={p} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
